import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import {Link} from 'react-router-dom';

import {ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {findSceneByOrderType} from 'app/utils/imageryUtils';
import {getOrgPrefix} from 'app/utils/organizationUtils';

import cs from './styles.module.scss';

import {VariableOrderType} from './index';

interface OrderTypeSwitcherProps {
  orderableScenes: I.List<I.ImmutableOf<ApiOrderableScene>>;
  orderableScene: I.ImmutableOf<ApiOrderableScene>;
  selectedOrderType: VariableOrderType;
  onOrderTypeChange: (type: VariableOrderType) => void;
  organization: I.ImmutableOf<ApiOrganization>;
}

const ORDER_TYPE_OPTIONS: Array<{value: VariableOrderType; label: string}> = [
  {value: 'truecolor', label: 'Truecolor'},
  {value: 'multispectral', label: 'Multispectral'},
];

export const OrderTypeSwitcher: React.FunctionComponent<OrderTypeSwitcherProps> = ({
  orderableScenes,
  orderableScene,
  selectedOrderType,
  onOrderTypeChange,
  organization,
}) => {
  const sensingTime = orderableScene.get('sensingTime');
  const sourceId = orderableScene.get('sourceId');

  const orderTypeOptionsWithDisabled = React.useMemo(() => {
    return ORDER_TYPE_OPTIONS.map((option) => {
      const scene = findSceneByOrderType(orderableScenes, sensingTime, sourceId, option.value);
      return {
        ...option,
        disabled: scene?.get('isFullyOrdered') ?? true,
        displayText: scene
          ? `${option.label} has already been purchased for this image`
          : `${option.label} scene not available`,
        disabledIcon: !!scene,
      };
    });
  }, [orderableScenes, sensingTime, sourceId]);

  const disabledOption = React.useMemo(() => {
    return orderTypeOptionsWithDisabled.find((option) => option.disabled);
  }, [orderTypeOptionsWithDisabled]);

  return (
    <>
      <div className={cs.typeSelector}>
        <B.SegmentedControl
          value={selectedOrderType}
          onValueChange={(value) => onOrderTypeChange(value as VariableOrderType)}
          options={orderTypeOptionsWithDisabled}
          fill
          large
        />

        {disabledOption?.displayText && (
          <p className={cs.disabledOptionHint}>
            {disabledOption.disabledIcon && <B.Icon icon="tick" size={12} />}
            {disabledOption.displayText}
          </p>
        )}
      </div>
      {selectedOrderType === 'multispectral' && (
        <B.Callout intent={B.Intent.PRIMARY} icon="info-sign" compact style={{marginTop: 0}}>
          <strong>Note:</strong> Multispectral imagery appears in Lens as data layers, such as
          Vegetation. Add the layers you want to view from the{' '}
          <Link
            to={`/${getOrgPrefix(organization)}/settings/layers`}
            target="_blank"
            rel="noreferrer"
          >
            Library
          </Link>
          . Downloads will contain all bands.
        </B.Callout>
      )}
    </>
  );
};
